import type { Platform } from "./db.js";

// Extract the platform-specific video id from a video URL. Used by manual video import to
// satisfy the `videos.UNIQUE(platform, platform_video_id)` constraint *before* we have the
// scrape result. Returns null when the URL doesn't match a recognised pattern — caller should
// surface that as a validation error.
//
// Patterns covered (best-effort; ignores trailing query strings, hash fragments):
//   tiktok:    https://www.tiktok.com/@user/video/<digits>
//              https://m.tiktok.com/v/<digits>.html
//              short-link forms (vt.tiktok.com/<short>, vm.tiktok.com/<short>,
//              www.tiktok.com/t/<short>) do NOT carry the numeric id in the path —
//              `extractPlatformVideoId` returns null. Detect with `isTiktokShortUrl`
//              and HTTP-resolve via redirect before re-extracting (see the import
//              handler in workers/admin-api/src/routes/videos.ts).
//   instagram: https://www.instagram.com/p/<code>/
//              https://www.instagram.com/reel(s)/<code>/
//              https://www.instagram.com/tv/<code>/
//   youtube:   https://www.youtube.com/shorts/<id>
//              https://www.youtube.com/watch?v=<id>
//              https://youtu.be/<id>
export function extractPlatformVideoId(platform: Platform, url: string): string | null {
  const u = url.trim();
  if (platform === "tiktok") {
    const m1 = u.match(/\/video\/(\d+)/);
    if (m1) return m1[1] ?? null;
    const m2 = u.match(/\/v\/(\d+)/);
    if (m2) return m2[1] ?? null;
    return null;
  }
  if (platform === "instagram") {
    const m = u.match(/\/(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/);
    return m ? (m[1] ?? null) : null;
  }
  if (platform === "youtube") {
    const m1 = u.match(/\/shorts\/([A-Za-z0-9_-]{6,})/);
    if (m1) return m1[1] ?? null;
    const m2 = u.match(/[?&]v=([A-Za-z0-9_-]{6,})/);
    if (m2) return m2[1] ?? null;
    const m3 = u.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
    if (m3) return m3[1] ?? null;
    return null;
  }
  return null;
}

// Strip the query string and hash fragment from a URL. Applied to every user-provided
// URL (video URLs, project page profile URLs) at schema-parse time so transient junk
// like `?_t=...` analytics tokens or `?si=...` share params don't end up persisted,
// fed to the scraper, or duplicating rows that point at the same canonical resource.
// Also applied a second time *after* the TikTok short-link resolver, since TikTok's
// redirector tacks fresh tracking params onto the canonical URL it 30x's to.
//
// Implementation note: a literal cut at the first `?` / `#` rather than `new URL()`
// roundtrip — `URL` isn't declared by the ES2022 lib this package targets, and the
// regex cut also avoids the side-effects of URL normalization (no auto-added trailing
// slash, no path encoding changes).
export function stripUrlParams(url: string): string {
  const trimmed = url.trim();
  const qIdx = trimmed.indexOf("?");
  const hIdx = trimmed.indexOf("#");
  let cut = -1;
  if (qIdx >= 0 && hIdx >= 0) cut = Math.min(qIdx, hIdx);
  else if (qIdx >= 0) cut = qIdx;
  else if (hIdx >= 0) cut = hIdx;
  return cut >= 0 ? trimmed.slice(0, cut) : trimmed;
}

// True for TikTok short-link forms whose numeric video id is NOT in the URL path
// (`vt.tiktok.com/<short>`, `vm.tiktok.com/<short>`, `www.tiktok.com/t/<short>`).
// Callers must HTTP-resolve these via redirect before passing to `extractPlatformVideoId`,
// otherwise extraction will return null. The HTTP resolver lives in the admin-api worker
// (so this package stays free of fetch / Workers-runtime dependencies).
export function isTiktokShortUrl(url: string): boolean {
  const u = url.trim();
  if (/^https?:\/\/(?:vt|vm)\.tiktok\.com\//i.test(u)) return true;
  if (/^https?:\/\/(?:www\.)?tiktok\.com\/t\//i.test(u)) return true;
  return false;
}

// Derive a canonical profile URL for a (platform, username) pair. Used by manual video
// import to populate creator_accounts.profile_url when auto-creating an account for a
// previously-unseen username. The resulting URL is "best-effort canonical" — if the user
// has a custom handle vs. legacy channel id (YouTube), the BD profile feed may not match,
// but for manual video imports we never run a profile feed against this URL anyway.
export function deriveProfileUrl(platform: Platform, username: string): string {
  const u = username.trim().replace(/^@/, "");
  if (platform === "tiktok") return `https://www.tiktok.com/@${u}`;
  if (platform === "instagram") return `https://www.instagram.com/${u}/`;
  return `https://www.youtube.com/@${u}`;
}
